import { fromJS } from 'immutable';
import { isNil } from 'lodash';
import {
  ADD_FIELD,
  BOOK_APPOINTMENT_TIME_SLOT_SUCCESS,
  CHECK_TIME_AVAILABLE,
  CLEAR_RESERVATIONS,
  FAILED_CHARGES,
  FETCH_FEE,
  FETCH_PROMO_CODE,
  INVALID_PROMO_CODE,
  PROCESS_PAYMENT,
  PROCESS_PAYMENT_SUCCESS,
  RECEIVE_RESERVATION,
  REQUEST_RESERVATION,
  RESET_FEE,
  RESET_FIELDS,
  RESET_PROCESS_PAYMENT,
  RESET_PROMO_CODE,
  RESET_TIP,
  SET_CUSTOM_TIP,
  SET_GIFT_CARD_CODE,
  SET_PROCESSING,
  SET_PROMO_CODE,
  SET_TIPS,
  SET_TIP,
  SET_SERVICES,
  TIME_AVAILABLE,
  TIME_UNAVAILABLE,
  TOUCH_FIELD,
  UPDATE_FIELD,
  VALIDATE_FIELD,
  CLEAR_ERROR,
  FAILED_BOOK_AND_RESERVE,
  RESCHEDULE_INVALID_PROMO_ERROR,
  SET_TAX_BREAKDOWN,
  RESET_PROMO_ERROR,
  REMOVE_PROMO_FROM_RESERVATION,
  SET_TOTAL,
  SET_TOTAL_WITHOUT_TIP_AND_FEES,
  VALIDATING_APPT_REQUEST_STARTED,
  VALIDATING_APPT_REQUEST_FINISHED,
  SET_MEMBERSHIP_DISCOUNTS,
} from '../constants';
import {
  isValidName,
  isValidEmail,
  isValidPhone,
  transformShopAddress,
  isValidLastName,
  isSameEmail,
} from '../utils/helpers';

const DEFAULT_FIELDS = [
  {
    id: 'firstName',
    name: 'fname',
    label: 'First name',
    errorMessage: 'You must enter first name',
    autoComplete: 'given-name',
    required: true,
  },
  {
    id: 'lastName',
    name: 'lname',
    label: 'Last name',
    errorMessage: 'You must enter last name',
    autoComplete: 'family-name',
    required: true,
  },
  {
    id: 'phone',
    name: 'phone',
    label: 'Phone number',
    errorMessage: 'You must enter valid phone number',
    autoComplete: 'tel-national',
    required: true,
    type: 'tel',
  },
  {
    id: 'email',
    name: 'email',
    label: 'Email address',
    errorMessage: 'You must enter valid email that you own',
    autoComplete: 'email',
    required: true,
    type: 'email',
  },
];

const initialState = fromJS({
  tip: null,
  tips: [],
  services: [],
  fields: DEFAULT_FIELDS,
  appointment: {},
  isFetching: false,
  paymentProcessing: false,
  timeAvailable: false,
  fees: 0,
  promo: {
    isValid: false,
    error: '',
    isFetching: false,
    amount: 0,
    promoCode: '',
    validationNote: '',
  },
  giftCard: {
    appliedAmount: null,
    remainingAmount: null,
  },
  customTip: null,
  total: 0,
  totalWithoutTipAndFees: 0,
  processing: false,
  errorMessage: null,
  taxBreakdown: [],
  outstandingAppointmentValidateRequests: [],
  membershipDiscounts: [],
});

function isFieldValid(id, value, countryCode, shop) {
  switch (id) {
    case 'phone':
      return isValidPhone(value, countryCode);

    case 'email': {
      // forbid using shop email and its variations for booking on prod
      const isAllowedEmail = process.env.WEB_ENV !== 'production' || !isSameEmail(shop.email, value);
      return isValidEmail(value) && isAllowedEmail;
    }

    case 'adultsonly':
      return value === true;

    case 'lastName':
      return isValidLastName(value);

    default:
      return isValidName(value);
  }
}

export default function reservationReducer(state = initialState, action) {
  switch (action.type) {
    case SET_TIPS:
      return state.withMutations(
        s => s.set('tips', action.payload)
      );

    case RESET_TIP:
      return state.withMutations(
        s => s.set('tip', initialState.get('tip')).set('customTip', initialState.get('customTip'))
      );

    case SET_TIP:
      return state.withMutations(
        (s) => {
          let tip = action.payload;
          if (action.payload === 0) {
            s.set('customTip', 0);
          } else if (action.payload === null) {
            tip = 0;
          } else if (action.payload !== '') {
            s.set('customTip', initialState.get('customTip'));
          }

          s.set('tip', tip);
        }
      );

    case SET_CUSTOM_TIP: {
      return state.withMutations(s => s.set('customTip', action.payload).set('tip', initialState.get('tip')));
    }

    case SET_SERVICES:
      return state.withMutations(
        s => s.set('services', action.payload)
      );

    case SET_TOTAL:
      return state.withMutations(
        s => s.set('total', action.payload)
      );

    case SET_TOTAL_WITHOUT_TIP_AND_FEES:
      return state.withMutations(
        s => s.set('totalWithoutTipAndFees', action.payload)
      );

    case ADD_FIELD: {
      return state.withMutations((s) => {
        const fields = state.get('fields').push(fromJS(action.payload));
        return s.set('fields', fields);
      });
    }

    case TOUCH_FIELD: {
      const index = state.get('fields').findIndex(field => field.get('id') === action.payload);
      return state.setIn(['fields', index, 'touched'], true);
    }

    case VALIDATE_FIELD: {
      const index = state.get('fields').findIndex(field => field.get('id') === action.payload.id);
      const field = state.get('fields').get(index);
      const id = field.get('id');
      const countryCode = action.payload.countryCode;

      return state.setIn(
        ['fields', index, 'error'],
        !isFieldValid(id, field.get('value'), countryCode, action.payload.shop)
      );
    }

    case UPDATE_FIELD: {
      const index = state.get('fields').findIndex(field => field.get('id') === action.payload.id);
      return state.withMutations(s => s.setIn(['fields', index, 'value'], action.payload.value));
    }

    case REQUEST_RESERVATION:
      return state.set('isFetching', true);

    case RECEIVE_RESERVATION:
    case BOOK_APPOINTMENT_TIME_SLOT_SUCCESS:
      return state.withMutations(
        s => s.set('isFetching', false)
        .set('appointment', fromJS(
          { ...action.payload, shop: transformShopAddress(action.payload.shop) }
        )));

    case CLEAR_RESERVATIONS:
      return state.set('appointment', {});

    case RESET_FIELDS:
      return state.set('fields', fromJS(DEFAULT_FIELDS));

    case PROCESS_PAYMENT:
      return state.set('paymentProcessing', true);

    case RESET_PROCESS_PAYMENT:
    case PROCESS_PAYMENT_SUCCESS:
    case FAILED_CHARGES:
      return state.set('paymentProcessing', false);

    case CHECK_TIME_AVAILABLE:
    case TIME_UNAVAILABLE:
      return state.set('timeAvailable', false);

    case VALIDATING_APPT_REQUEST_STARTED:
      return state.updateIn(['outstandingAppointmentValidateRequests'], list => list.push(action.payload));

    case VALIDATING_APPT_REQUEST_FINISHED:
      return state.set(
        'outstandingAppointmentValidateRequests',
        state.get('outstandingAppointmentValidateRequests').filter(id => id !== action.payload)
      );

    case TIME_AVAILABLE:
      return state.set('timeAvailable', true);

    case RESET_PROMO_CODE:
      return state.set('promo', initialState.get('promo')).set('giftCard', initialState.get('giftCard'));

    case REMOVE_PROMO_FROM_RESERVATION:
      return state.setIn(['promo', 'promoCode'], initialState.get('promo').promoCode);

    case RESET_PROMO_ERROR:
      return state.setIn(['promo', 'error'], initialState.get('promo').error);

    case SET_PROMO_CODE:
      return state
        .setIn(['promo', 'amount'], action.payload.amount || 0)
        .setIn(['promo', 'promoCode'], action.payload.promoCode)
        .setIn(['promo', 'isValid'], !isNil(action.payload.amount))
        .setIn(['promo', 'error'], false)
        .setIn(['promo', 'isFetching'], isNil(action.payload.amount))
        .setIn(['promo', 'validationNote'], action.payload.validationNote)
        .setIn(['giftCard', 'appliedAmount'], null)
        .setIn(['giftCard', 'remainingAmount'], null);

    case SET_GIFT_CARD_CODE:
      return state
        .setIn(['promo', 'amount'], 0)
        .setIn(['promo', 'promoCode'], action.payload.promoCode)
        .setIn(['promo', 'isValid'], true)
        .setIn(['promo', 'error'], false)
        .setIn(['promo', 'isFetching'], false)
        .setIn(['giftCard', 'appliedAmount'], action.payload.appliedAmount || 0)
        .setIn(['giftCard', 'remainingAmount'], action.payload.remainingAmount || 0);

    case FETCH_PROMO_CODE:
      return state.setIn(['promo', 'isFetching'], true);

    case SET_MEMBERSHIP_DISCOUNTS:
      return state.set('membershipDiscounts', action.payload);

    case INVALID_PROMO_CODE:
      return state
        .setIn(['promo', 'isValid'], false)
        .setIn(['promo', 'error'], action.payload)
        .setIn(['promo', 'isFetching'], false);

    case RESET_FEE:
      return state.set('fees', initialState.fees);

    case FETCH_FEE:
      return state.set('fees', action.payload);

    case SET_PROCESSING:
      return state.set('processing', Boolean(action.payload));

    case FAILED_BOOK_AND_RESERVE:
    case RESCHEDULE_INVALID_PROMO_ERROR:
      return state.set('errorMessage', action.payload);

    case CLEAR_ERROR:
      return state.set('errorMessage', null);
    case SET_TAX_BREAKDOWN:
      return state.set('taxBreakdown', fromJS(action.payload));
    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/app/reservation/reducer.js